'use client';

import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    ReactNode,
} from 'react';
import {
    User,
    onAuthStateChanged as firebaseOnAuthStateChanged,
    Auth,
} from 'firebase/auth';
import { auth as authPromise } from '@/lib/firebaseAuth'; // authPromise is a Promise<Auth>

/**
 * @interface AuthContextType
 * Defines the shape of the data provided by the AuthContext.
 */
interface AuthContextType {
    /** The current Firebase user object, or null if the user is not logged in. */
    user: User | null;
    /** Boolean indicating if the initial authentication state is still being determined. */
    isLoading: boolean;
    /** Boolean indicating if the current user is an anonymous user. */
    isAnonymous: boolean;
    /** Boolean indicating if the user's current plan allows streaming responses (null if undetermined). */
    allowStreaming: boolean | null; // Added
    /** The resolved Firebase Auth instance, or null if not available/loaded. */
    authInstance: Auth | null; // Added
    // TODO: Add subscription/plan data later if needed (from Task 3.1 spec)
    // subscriptionData?: any; // Placeholder
    // isSubscriptionLoading?: boolean; // Placeholder
    // authInstance: Auth | null; // Optionally expose if needed elsewhere
}

/**
 * React Context for managing and providing authentication state.
 */
const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Custom hook to access the authentication context.
 * Ensures the hook is used within an AuthProvider.
 * @returns {AuthContextType} The authentication context data.
 * @throws {Error} If used outside of an AuthProvider.
 */
export function useAuth(): AuthContextType {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}

/**
 * @interface AuthProviderProps
 * Defines the props accepted by the AuthProvider component.
 */
interface AuthProviderProps {
    /** The child components that the AuthProvider will wrap. */
    children: ReactNode;
}

/**
 * Provides authentication state to its children components.
 * Listens to Firebase Authentication state changes and updates the context.
 * Handles the initial loading state display.
 * @param {AuthProviderProps} props - The component props.
 * @returns {React.ReactElement} The provider component wrapping its children or a loading indicator.
 */
export function AuthProvider({
    children,
}: AuthProviderProps): React.ReactElement {
    const [user, setUser] = useState<User | null>(null);
    // isLoading is primarily for client-side UI updates after initial render
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isAnonymous, setIsAnonymous] = useState(false);
    const [allowStreaming, setAllowStreaming] = useState<boolean | null>(null);
    const [authInstance, setAuthInstance] = useState<Auth | null>(null);

    useEffect(() => {
        console.log('[AuthProvider] Attempting to resolve authPromise...');
        authPromise
            .then((resolvedAuth) => {
                console.log(
                    '[AuthProvider] authPromise resolved. Auth instance obtained.'
                );
                setAuthInstance(resolvedAuth);
            })
            .catch((error) => {
                console.error(
                    '[AuthProvider] Failed to initialize Firebase Auth from authPromise:',
                    error
                );
                setIsLoading(false); // Stop loading if auth initialization fails
            });
    }, []); // Runs once on mount

    useEffect(() => {
        if (authInstance) {
            console.log(
                '[AuthProvider] authInstance resolved. Setting up Firebase Auth state listener.'
            );
            const unsubscribe = firebaseOnAuthStateChanged(
                authInstance, // Use the resolved Auth instance
                (currentUser) => {
                    setUser(currentUser);
                    setIsAnonymous(currentUser?.isAnonymous ?? false);
                    setIsLoading(false); // Auth state is now known
                    console.log(
                        '[AuthProvider] Auth state changed via listener:',
                        currentUser?.uid ?? 'logged out'
                    );
                    setAllowStreaming(null); // Reset or determine based on user plan
                },
                (error) => {
                    console.error(
                        '[AuthProvider] Error in onAuthStateChanged listener:',
                        error
                    );
                    setUser(null);
                    setIsLoading(false); // Listener error, stop loading
                    setAllowStreaming(null);
                }
            );
            return () => {
                console.log(
                    '[AuthProvider] Unsubscribing Firebase Auth state listener.'
                );
                unsubscribe();
            };
        } else {
            console.log(
                '[AuthProvider] authInstance not yet resolved. Waiting to set up listener.'
            );
            // isLoading remains true until authInstance is set and onAuthStateChanged provides first state
        }
    }, [authInstance]); // This effect depends on authInstance

    const value: AuthContextType = {
        user,
        isLoading,
        isAnonymous,
        allowStreaming,
        authInstance,
    };

    // Always render children. Client components within children will use the
    // isLoading state from the context to show their own loading states if needed.
    return (
        <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
    );
}
